import React, { useEffect, useState, useRef } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Formik } from "formik";
import * as Yup from "yup";
import { createForIndividualsPara, getForIndividualsPara, updateForIndividualsPara } from "../../../api/axios";
import CmsCircularProgress from "../../Common/CircularProgress";
import removeIcon from "../../../Assets/remove-image-photo.svg";
import { showToast } from "../../../Utills/toast";
import FormGroup from "../../Common/FormGroup";

// Validation Schema
const validationSchema = Yup.object({
  paragraph: Yup.string()
    .required("Paragraph is required"),
  headerImage: Yup.mixed()
    .test("required", "Header image is required", function (value) {
      const { old_headerImage } = this.parent;
      return !!(value || old_headerImage);
    }),
});

export default function EditPara() {
  const { id, partnerId } = useParams();
  const [data, setPara] = useState({
    paragraph: "",
    headerImage: "",
    index: null,
  });
  const inputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const getInitialValues = () => ({
    paragraph: data.paragraph || "",
    headerImage: data.headerImage || "",
    old_headerImage: data.headerImage || "",
  });

  const fetchParaData = async () => {
    setIsLoading(true);
    if (!Number(id)) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await getForIndividualsPara(id);
      console.log(res);
      const individualData = res.data.data[0];
      setPara({
        ...individualData,
        index: res.data.data.indexOf(individualData),
      });
    } catch (error) {
      console.log(error);
      showToast({
        type: 'error',
        message: error?.response?.data?.message || "Failed to fetch paragraph data. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchParaData();
  }, [id]);

  const handleSubmit = async (values, { setFieldValue }) => {
    setSaving(true);

    const formData = new FormData();
    formData.append("paragraph", values.paragraph);

    if (values.headerImage instanceof File) {
      formData.append("headerImage", values.headerImage);
    }

    try {
      if (!Number(id)) {
        formData.append("partner_id", partnerId);
        const res = await createForIndividualsPara(formData);
        console.log(res);
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: res?.data?.message || 'Paragraph section has been created successfully!',
          });
          const individualData = res.data.data;
          setPara(individualData);
          if (individualData && individualData.id) {
            navigate(`/${partnerId}/forindividuals/para/edit/${individualData.id}`);
          }
        } else {
          showToast({
            type: 'error',
            message: res?.data?.message || 'Failed to create paragraph section. Please try again.',
          });
        }
      } else {
        const res = await updateForIndividualsPara(id, formData);
        console.log(res);
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: res?.data?.message || 'Paragraph section has been updated successfully!',
          });
          
          // Update form values with response data
          const updatedData = res?.data?.data;
          if (updatedData) {
            setFieldValue("headerImage", updatedData.headerImage);
            setFieldValue("old_headerImage", updatedData.headerImage);
            setFieldValue("paragraph", updatedData.paragraph);
            
            setPara(prev => ({
              ...prev,
              ...updatedData,
            }));
            
            // Clear file input
            if (inputRef.current) {
              inputRef.current.value = '';
            }
          }
        } else {
          showToast({
            type: 'error',
            message: res?.data?.message || 'Failed to update paragraph section. Please try again.',
          });
        }
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: err?.response?.data?.message || 'Failed to update paragraph section. Please try again.',
      });
      console.log(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {isLoading ? <CmsCircularProgress /> : 
        <FormWrapper>
          <Title>{Number(id) ? 'Edit' : 'Add'} Paragraph section</Title>
          <SliderDiv>
            <Formik
              initialValues={getInitialValues()}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({
                values,
                errors,
                touched,
                handleSubmit,
                handleChange,
                handleBlur,
                setFieldValue,
                isSubmitting,
              }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  <FormGroup 
                    label='Enter a new Paragraph' 
                    requiredField
                    error={touched.paragraph && errors.paragraph ? errors.paragraph : ''}
                  >
                    <Form.Control 
                      as="textarea"
                      rows={3}
                      name="paragraph"
                      value={values.paragraph}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter paragraph here..."
                      isInvalid={touched.paragraph && !!errors.paragraph}
                    />
                  </FormGroup>

                  <FormGroup
                    label='Select a new Image'
                    requiredField
                    error={touched.headerImage && errors.headerImage ? errors.headerImage : ''}
                  >
                    <Form.Control
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setFieldValue("headerImage", file);
                      }}
                      style={{ display: "none" }}
                      ref={inputRef}
                      name="headerImage"
                      isInvalid={touched.headerImage && !!errors.headerImage}
                    />
                    {values.headerImage ? (
                      <ImageWrapper>
                        <UploadImage>
                          <BootstrapImage
                            src={values.headerImage instanceof File ? URL.createObjectURL(values.headerImage) : values.headerImage}
                            alt="headerImage"
                            onClick={() => {
                              inputRef.current.click();
                            }}
                          />
                        </UploadImage>
                        {(values.headerImage instanceof File || (values.headerImage && values.headerImage !== values.old_headerImage)) && (
                          <RemoveButtonWrapper>
                            <BootstrapButton 
                              type="button"
                              onClick={() => {
                                setFieldValue("headerImage", values.old_headerImage || null);
                                if (inputRef.current) {
                                  inputRef.current.value = '';
                                }
                              }}
                            >
                              <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                            </BootstrapButton>
                          </RemoveButtonWrapper>
                        )}
                      </ImageWrapper>
                    ) : (Number(id) && (values.headerImage || values.old_headerImage)) ? (
                      <UploadImage>
                        <BootstrapImage
                          src={values.headerImage || values.old_headerImage}
                          alt="headerImage"
                          onClick={() => {
                            inputRef.current.click();
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.opacity = 0.8;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.opacity = 1;
                          }}
                        />
                      </UploadImage>
                    ) : (
                      <Button className="mb-0" onClick={() => {
                        inputRef.current.click();
                      }}>Upload the image</Button>
                    )}
                  </FormGroup>

                  <ButtonWrapper className="mt-3">
                    <Link to={`/${partnerId}/forindividuals/para/view/${Number(id) ? id : 'create'}`}>
                      <Button variant="secondary" className="w-100">Go Back</Button>
                    </Link>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={saving || isSubmitting}
                      className="w-100"
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </Button>
                  </ButtonWrapper>
                </Form>
              )}
            </Formik>
          </SliderDiv>
        </FormWrapper>
      }
    </>
  );
}

const UploadImage = styled.div`
  max-width: 80vw;
  overflow: hidden;
  border-radius: 6px;
  @media (max-width:576px) {
    max-width: 100vw;
  }
`;

const BootstrapImage = styled('img')(({ theme }) => ({
  maxWidth: "80vw",
  cursor: "pointer",
  "@media (max-width: 576px)": {
    maxWidth: "100vw",
  },
}));

const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const FormWrapper = styled.div`
  @media (min-width:576px) {
    padding: 1rem;
  }
  @media (min-width:768px) {
    padding: 1.5rem;
  }
  padding: .5rem;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  @media (max-width: 890px) {
    gap: 2rem;
  }
  @media (max-width: 425px) {
    gap: 1rem;
  }
`;

const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  position: absolute;
  top: -17px;
  right: -14px;
  @media (max-width:576px) {
    top: -9px;
    right: -17px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  max-width: 80vw;
  @media (max-width:576px) {
    max-width: 100vw;
  }
`;

const BootstrapButton = styled('button')(({ theme }) => ({
  margin: '0 !important',
  backgroundColor:'#FFFFFF',
  padding: '5px 7px',
  border: '1px solid #dc3545',
  "&:hover": {
    background: '#fffeff',
    boxShadow:'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px'
  },
  '@media (max-width:576px)': {
    padding: '2px 4px',
  },
}));